// Este arquivo conterá a conversão do componente `Parishes` para MUI.
// Começaremos pela estrutura básica e filtros

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  CircularProgress,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Divider,
  Alert,
  Tabs,
  Tab,
  Chip,
  Tooltip,
  TableContainer,
  Stack,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { parishService } from '../services';
import { Parish, ParishFilters } from '../../../types/types';
import { useToast } from '../hook/use-toast';
import {
  FilterList,
  Visibility,
  Settings as SettingsIcon,
  MailOutline as MailOutlineIcon,
  CloudSync as CloudSyncIcon,
  Refresh as RefreshIcon,
  FactCheckOutlined as FactCheckOutlinedIcon,
  OpenInNew as OpenInNewIcon,
  InfoOutlined as InfoOutlinedIcon,
  MenuBookOutlined as MenuBookOutlinedIcon,
} from '@mui/icons-material';
import ParishRegistrationRequest from '../../../pages/events/ParishRegistrationRequest';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { normalizeWithdrawalPanelMode } from '../../../utils/parishWithdrawalPanel';
import PagarmeSplitStatusPanel from './PagarmeSplitStatusPanel';
import { legalService, triggerBlobDownload } from '../../../services/legalService';

type ParishListTab = 'all' | 'emailMismatch' | 'recipientApproval' | 'splitStatus';

type ParishListRow = Parish & {
  pagarMeRecipientId?: string | null;
  pagarmeRecipientProfile?: {
    onboardingStatus?: string;
    providerStatus?: string | null;
  };
};

function parishSplitStatusCell(parish: ParishListRow) {
  const onboarding = parish.pagarmeRecipientProfile?.onboardingStatus;
  const provider = parish.pagarmeRecipientProfile?.providerStatus;
  const hasRecipient = !!String(parish.pagarMeRecipientId || '').trim();
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {onboardingStatusChip(onboarding)}
      {provider ? (
        <Chip
          size="small"
          label={providerStatusLabel(provider)}
          color={String(provider).toLowerCase() === 'refused' ? 'error' : 'default'}
          variant="outlined"
        />
      ) : hasRecipient ? (
        <Chip size="small" label="PSP não consultado" variant="outlined" />
      ) : null}
    </Stack>
  );
}

type ParishAdminRow = {
  _id: string;
  name: string;
  email: string;
  allowWithdrawalAccess?: boolean;
  allowAdditionalOrganizations?: boolean;
};

type ParishEmailAlignment = {
  primaryAdminEmail: string | null;
  parishEmail: string | null;
  matchesPrimaryAdmin: boolean;
};

function sectorLimitDraft(features?: Parish['features']): { allowCreateSubgroup: boolean; maxSubParishes: number } {
  const stored = features?.maxSubParishes;
  const enabled = !!features?.allowCreateSubgroup || (typeof stored === 'number' && stored > 0);
  const limit = typeof stored === 'number' && stored > 0 ? stored : enabled ? 5 : 1;
  return { allowCreateSubgroup: enabled, maxSubParishes: limit };
}

type ParishDetailPayload = Parish & {
  parishAdmins?: ParishAdminRow[];
  parishAdmin?: {
    name?: string;
    email?: string;
    active?: boolean;
    emailVerifiedAt?: string | Date | null;
  };
  emailAlignment?: ParishEmailAlignment;
};

/** Alinhado ao query `view` em `GET /admin/parishes/pagarme-onboarding/pending`. */
type RecipientQueueView =
  | 'pending'
  | 'ready'
  | 'error'
  | 'draft'
  | 'pending_sync'
  | 'provider_refused';

type RecipientApprovalRow = {
  _id: string;
  name: string;
  city?: string;
  state?: string;
  email?: string;
  primaryAdminEmail?: string | null;
  createdAt?: string | null;
  pagarMeRecipientId?: string | null;
  onboardingStatus?: string;
  providerStatus?: string | null;
  providerStatusReason?: string | null;
  providerStatusCheckedAt?: string | null;
  lastSyncError?: string | null;
  lastSyncedAt?: string | null;
  hasPixWithdrawalKey?: boolean;
};

const POLL_APPROVAL_MS = 45_000;

function parishContactLines(parishEmail?: string | null, loginEmail?: string | null) {
  const contact = String(parishEmail || '').trim();
  const login = String(loginEmail || '').trim();
  const same =
    contact && login && contact.toLowerCase() === login.toLowerCase();
  return (
    <>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
        Login admin: {login || '—'}
      </Typography>
      {contact && !same ? (
        <Typography variant="caption" color="text.disabled" display="block">
          Contato paróquia: {contact}
        </Typography>
      ) : null}
      {!contact && !login ? (
        <Typography variant="caption" color="text.disabled" display="block">
          Sem e-mails cadastrados
        </Typography>
      ) : null}
    </>
  );
}

function onboardingStatusChip(status: string | undefined) {
  const s = String(status || 'draft').toLowerCase();
  const label =
    s === 'draft'
      ? 'Rascunho'
      : s === 'pending_sync'
        ? 'Validado (aguardando sync)'
        : s === 'ready'
          ? 'Pronto'
          : s === 'error'
            ? 'Erro'
            : status || '—';
  const color =
    s === 'error' ? ('error' as const) : s === 'pending_sync' ? ('warning' as const) : s === 'ready' ? ('success' as const) : ('default' as const);
  return <Chip size="small" label={label} color={color} variant={s === 'draft' ? 'outlined' : 'filled'} />;
}

function providerStatusLabel(raw: string | null | undefined): string {
  if (raw == null || String(raw).trim() === '') return '—';
  const s = String(raw).toLowerCase();
  if (['active', 'approved', 'enabled', 'verified', 'accepted'].includes(s)) return `Ativo (${raw})`;
  if (['registration', 'affiliation', 'pending', 'waiting', 'analyzing', 'processing'].includes(s))
    return `Pendente (${raw})`;
  if (['refused', 'rejected', 'denied', 'blocked', 'disabled'].includes(s)) return `Recusado (${raw})`;
  return String(raw);
}

function primaryAdminEmailVerifiedChip(at: string | Date | null | undefined) {
  const raw = at == null ? null : at;
  const verified = raw != null && String(raw).trim() !== '';
  const label = verified ? 'Sim' : 'Não';
  const title = verified
    ? `Confirmado em ${new Date(raw as string | Date).toLocaleString('pt-BR')}`
    : 'E-mail de login do admin principal ainda não confirmado (bloqueio EMAIL_NOT_VERIFIED no painel)';
  return (
    <Tooltip title={title}>
      <Chip
        size="small"
        label={label}
        color={verified ? 'success' : 'warning'}
        variant={verified ? 'filled' : 'outlined'}
      />
    </Tooltip>
  );
}

const ParishesMUI: React.FC = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const initialListTab: ParishListTab =
    tabFromUrl === 'emailMismatch' ||
    tabFromUrl === 'recipientApproval' ||
    tabFromUrl === 'splitStatus'
      ? tabFromUrl
      : 'all';
  const [filters, setFilters] = useState<ParishFilters>({
    page: 1,
    limit: 500,
    search: '',
    status: undefined,
    state: '',
  });

  const [openCreate, setOpenCreate] = useState(false);
  const [parishForDetails, setParishForDetails] = useState<Parish | null>(null);
  const [parishForFeatures, setParishForFeatures] = useState<Parish | null>(null);
  const [featuresDraft, setFeaturesDraft] = useState<{
    allowEarlyWithdrawalRequest: boolean;
    allowRefundRequest: boolean;
    allowCreateSubgroup: boolean;
    maxSubParishes: number;
    customDomainPagesEnabled: boolean;
    pagarmePixCheckoutEnabled: boolean;
    withdrawalPanelMode: 'released' | 'hidden' | 'message';
    withdrawalBlockedMessage: string;
  }>({
    allowEarlyWithdrawalRequest: false,
    allowRefundRequest: false,
    allowCreateSubgroup: false,
    maxSubParishes: 0,
    customDomainPagesEnabled: false,
    pagarmePixCheckoutEnabled: true,
    withdrawalPanelMode: 'released',
    withdrawalBlockedMessage: '',
  });
  const [adminsDraft, setAdminsDraft] = useState<Record<string, boolean>>({});
  const [additionalOrgsDraft, setAdditionalOrgsDraft] = useState<Record<string, boolean>>({});
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [listTab, setListTab] = useState<ParishListTab>(initialListTab);

  const parishQueryFilters = useMemo((): ParishFilters => {
    const base: ParishFilters = { ...filters };
    if (listTab === 'emailMismatch') {
      base.emailMismatch = true;
    } else {
      delete base.emailMismatch;
    }
    return base;
  }, [filters, listTab]);

  const [recipientQueueView, setRecipientQueueView] = useState<RecipientQueueView>('pending');

  const { data: parishesData, isLoading, error } = useQuery({
    queryKey: ['parishes', parishQueryFilters],
    queryFn: () => parishService.getAll(parishQueryFilters),
    enabled: listTab !== 'recipientApproval',
  });

  const recipientQueueSubheader = useMemo(() => {
    switch (recipientQueueView) {
      case 'ready':
        return 'Paróquias principais ativas com recebedor pronto no Pagar.me (onboarding “ready”). A lista só lê o banco (~45s).';
      case 'error':
        return 'Paróquias com falha local de onboarding (status “error”), ainda sem sync completo. Consulta só o banco (~45s).';
      case 'draft':
        return 'Paróquias em rascunho (sem status ou “draft”), ainda sem recebedor pronto. Consulta só o banco (~45s).';
      case 'pending_sync':
        return 'Paróquias com status “pending_sync”: dados enviados, aguardando conclusão. Consulta só o banco (~45s).';
      case 'provider_refused':
        return 'Paróquias com último status do Pagar.me indicando recusa / bloqueio (campo gravado no cadastro). Consulta só o banco (~45s).';
      case 'pending':
      default:
        return 'Paróquias ativas ainda sem recebedor pronto no Pagar.me (split). A lista atualiza sozinha a cada ~45s — só consulta o banco, sem custo de API.';
    }
  }, [recipientQueueView]);

  const recipientQueueEmptyHint = useMemo(() => {
    switch (recipientQueueView) {
      case 'ready':
        return 'Nenhuma paróquia principal ativa aparece como “recebedor pronto” neste momento.';
      case 'error':
        return 'Nenhuma paróquia com status local de erro (onboarding error).';
      case 'draft':
        return 'Nenhuma paróquia em rascunho neste filtro.';
      case 'pending_sync':
        return 'Nenhuma paróquia em “pending_sync”.';
      case 'provider_refused':
        return 'Nenhuma paróquia com status recusado/bloqueado registrado — use “consultar na API” nas linhas com ID quando precisar preencher o status.';
      case 'pending':
      default:
        return 'Nenhuma paróquia na fila neste modo. Todas as principais ativas já têm recebedor pronto, ou há outro filtro (somente paróquia principal).';
    }
  }, [recipientQueueView]);

  const approvalQueueQuery = useQuery({
    queryKey: ['parish-recipient-approval-queue', recipientQueueView],
    queryFn: () => parishService.getPagarmeOnboardingPending(recipientQueueView),
    enabled: listTab === 'recipientApproval',
    refetchInterval: listTab === 'recipientApproval' ? POLL_APPROVAL_MS : false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 25_000,
  });

  const handleListTabChange = useCallback(
    (_e: React.SyntheticEvent, value: ParishListTab) => {
      setListTab(value);
      setFilters((prev) => ({ ...prev, page: 1 }));
      if (value === 'all') {
        setSearchParams({}, { replace: true });
      } else {
        setSearchParams({ tab: value }, { replace: true });
      }
    },
    [setSearchParams]
  );

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t === 'emailMismatch' || t === 'recipientApproval' || t === 'splitStatus') {
      setListTab(t);
    } else if (!t) {
      setListTab('all');
    }
  }, [searchParams]);

  const handleFilterChange = (key: keyof ParishFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleOpenCreate = () => setOpenCreate(true);
  const handleCloseCreate = () => {
    setOpenCreate(false);
    queryClient.invalidateQueries({ queryKey: ['parishes'] });
  };

  const handleOpenFeatures = (parish: Parish) => {
    setParishForFeatures(parish);
    setAdminsDraft({});
    setFeaturesDraft({
      allowEarlyWithdrawalRequest: !!(parish.features?.allowEarlyWithdrawalRequest),
      allowRefundRequest: !!(parish.features?.allowRefundRequest),
      ...sectorLimitDraft(parish.features),
      customDomainPagesEnabled: !!(parish.features?.customDomainPagesEnabled),
      pagarmePixCheckoutEnabled: parish.features?.pagarmePixCheckoutEnabled !== false,
      withdrawalPanelMode: normalizeWithdrawalPanelMode(parish.features?.withdrawalPanelMode),
      withdrawalBlockedMessage: parish.features?.withdrawalBlockedMessage || '',
    });
  };
  const handleCloseFeatures = () => {
    setParishForFeatures(null);
  };

  const handleOpenDetails = (parish: Parish) => setParishForDetails(parish);
  const handleCloseDetails = () => setParishForDetails(null);

  const { data: parishDetailModal, isLoading: parishDetailModalLoading } = useQuery({
    queryKey: ['parish-detail-modal', parishForDetails?._id],
    queryFn: async () => {
      const res = await parishService.getById(parishForDetails!._id);
      return res.data as ParishDetailPayload;
    },
    enabled: Boolean(parishForDetails?._id),
  });

  const alignEmailMutation = useMutation({
    mutationFn: (id: string) => parishService.alignEmailWithPrimaryAdmin(id),
    onSuccess: (res) => {
      if (res.success) {
        toast({ title: res.message || 'Email alinhado.' });
        queryClient.invalidateQueries({ queryKey: ['parish-detail-modal', parishForDetails?._id] });
        queryClient.invalidateQueries({ queryKey: ['parishes'] });
      }
    },
    onError: () => {
      toast({ title: 'Não foi possível alinhar o email.', variant: 'destructive' });
    },
  });

  const alignPrimaryAdminEmailMutation = useMutation({
    mutationFn: (id: string) => parishService.alignPrimaryAdminEmailWithParish(id),
    onSuccess: (res) => {
      if (res.success) {
        toast({ title: res.message || 'Login do admin atualizado.' });
        queryClient.invalidateQueries({ queryKey: ['parish-detail-modal', parishForDetails?._id] });
        queryClient.invalidateQueries({ queryKey: ['parishes'] });
      }
    },
    onError: (e: any) => {
      toast({
        title: e?.response?.data?.message || 'Não foi possível atualizar o login do admin.',
        variant: 'destructive',
      });
    },
  });

  const verifyPrimaryAdminEmailMutation = useMutation({
    mutationFn: (id: string) => parishService.verifyPrimaryParishAdminEmail(id),
    onSuccess: (res) => {
      if (res.success) {
        toast({ title: res.message || 'E-mail marcado como verificado.' });
        queryClient.invalidateQueries({ queryKey: ['parish-features-detail', parishForFeatures?._id] });
        queryClient.invalidateQueries({ queryKey: ['parishes'] });
      }
    },
    onError: (e: any) => {
      toast({
        title: e?.response?.data?.message || 'Não foi possível marcar o e-mail como verificado.',
        variant: 'destructive',
      });
    },
  });

  const { data: parishDetailResponse, isLoading: parishFeaturesLoading } = useQuery({
    queryKey: ['parish-features-detail', parishForFeatures?._id],
    queryFn: async () => {
      const res = await parishService.getById(parishForFeatures!._id);
      return res.data as unknown as ParishDetailPayload;
    },
    enabled: Boolean(parishForFeatures?._id),
  });

  const parishAdmins = parishDetailResponse?.parishAdmins;

  useEffect(() => {
    if (!parishDetailResponse?.parishAdmins) return;
    const next: Record<string, boolean> = {};
    const nextOrgs: Record<string, boolean> = {};
    for (const a of parishDetailResponse.parishAdmins) {
      next[String(a._id)] = a.allowWithdrawalAccess !== false;
      nextOrgs[String(a._id)] = a.allowAdditionalOrganizations === true;
    }
    setAdminsDraft(next);
    setAdditionalOrgsDraft(nextOrgs);
    setFeaturesDraft({
      allowEarlyWithdrawalRequest: !!parishDetailResponse.features?.allowEarlyWithdrawalRequest,
      allowRefundRequest: !!parishDetailResponse.features?.allowRefundRequest,
      ...sectorLimitDraft(parishDetailResponse.features),
      customDomainPagesEnabled: !!parishDetailResponse.features?.customDomainPagesEnabled,
      pagarmePixCheckoutEnabled: parishDetailResponse.features?.pagarmePixCheckoutEnabled !== false,
      withdrawalPanelMode: normalizeWithdrawalPanelMode(parishDetailResponse.features?.withdrawalPanelMode),
      withdrawalBlockedMessage: parishDetailResponse.features?.withdrawalBlockedMessage || '',
    });
  }, [parishDetailResponse]);

  const hasAnyWithdrawalAccess = useMemo(() => {
    if (!parishAdmins?.length) return false;
    return parishAdmins.some((a) => adminsDraft[String(a._id)]);
  }, [parishAdmins, adminsDraft]);

  const onboardingPendingQuery = useQuery({
    queryKey: ['pagarme-onboarding-pending'],
    queryFn: () => parishService.getPagarmeOnboardingPending('pending'),
    enabled: onboardingOpen,
  });

  const sendOnboardingMutation = useMutation({
    mutationFn: (pid: string) => parishService.sendPagarmeOnboardingEmail(pid),
    onSuccess: () => {
      toast({ title: 'E-mail enviado' });
      queryClient.invalidateQueries({ queryKey: ['pagarme-onboarding-pending'] });
      queryClient.invalidateQueries({ queryKey: ['parish-recipient-approval-queue'] });
    },
    onError: () => toast({ title: 'Falha ao enviar', variant: 'destructive' }),
  });

  const adminSyncRecipientMutation = useMutation({
    mutationFn: ({ id, dryRun }: { id: string; dryRun: boolean }) =>
      parishService.adminSyncPagarmeRecipient(id, { dryRun }),
    onSuccess: (res, vars) => {
      if (res.success) {
        toast({ title: res.message || (vars.dryRun ? 'Validação concluída.' : 'Sincronizado.') });
      } else {
        toast({
          title: (res as { message?: string }).message || 'Não foi possível concluir.',
          variant: 'destructive',
        });
      }
      queryClient.invalidateQueries({ queryKey: ['parish-recipient-approval-queue'] });
      queryClient.invalidateQueries({ queryKey: ['parishes'] });
    },
    onError: (e: any) => {
      toast({
        title: e?.response?.data?.message || 'Falha na sincronização',
        variant: 'destructive',
      });
      queryClient.invalidateQueries({ queryKey: ['parish-recipient-approval-queue'] });
    },
  });

  const refreshProviderStatusMutation = useMutation({
    mutationFn: (parishId: string) => parishService.getPagarmeRecipientStatus(parishId),
    onSuccess: (res) => {
      if (res.success) {
        toast({
          title: `Pagar.me: ${res.data?.providerStatus ?? 'ok'}`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['parish-recipient-approval-queue'] });
    },
    onError: (e: any) => {
      toast({
        title: e?.response?.data?.message || 'Não foi possível consultar o Pagar.me',
        variant: 'destructive',
      });
    },
  });

  const saveFeaturesMutation = useMutation({
    mutationFn: async () => {
      if (!parishForFeatures?._id || !parishDetailResponse) {
        throw new Error('Dados da paróquia incompletos');
      }
      const admins = parishDetailResponse.parishAdmins ?? [];
      for (const a of admins) {
        const want = adminsDraft[String(a._id)] ?? false;
        await parishService.updateParishAdminWithdrawalAccess(String(a._id), want);
        const wantOrgs = additionalOrgsDraft[String(a._id)] ?? false;
        await parishService.updateParishAdminAdditionalOrganizations(String(a._id), wantOrgs);
      }
      const anyAccess = admins.some((a) => adminsDraft[String(a._id)]);
      await parishService.update(parishForFeatures._id, {
        features: {
          ...(parishDetailResponse.features || {}),
          allowEarlyWithdrawalRequest: anyAccess && featuresDraft.allowEarlyWithdrawalRequest,
          allowRefundRequest: featuresDraft.allowRefundRequest,
          allowCreateSubgroup: featuresDraft.allowCreateSubgroup,
          maxSubParishes: featuresDraft.allowCreateSubgroup
            ? Math.max(1, Math.min(100, Number(featuresDraft.maxSubParishes) || 1))
            : 0,
          customDomainPagesEnabled: featuresDraft.customDomainPagesEnabled,
          pagarmePixCheckoutEnabled: featuresDraft.pagarmePixCheckoutEnabled,
          withdrawalPanelMode: featuresDraft.withdrawalPanelMode,
          withdrawalBlockedMessage:
            featuresDraft.withdrawalPanelMode === 'message'
              ? String(featuresDraft.withdrawalBlockedMessage).trim()
              : '',
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parishes'] });
      queryClient.invalidateQueries({ queryKey: ['parish-features-detail', parishForFeatures?._id] });
      toast({ title: 'Funcionalidades atualizadas' });
      handleCloseFeatures();
    },
    onError: () => {
      toast({ title: 'Erro ao atualizar funcionalidades', variant: 'destructive' });
    },
  });

  const handleSaveFeatures = () => {
    if (!parishForFeatures || !parishDetailResponse) return;
    if (
      featuresDraft.withdrawalPanelMode === 'message' &&
      !String(featuresDraft.withdrawalBlockedMessage).trim()
    ) {
      toast({
        title: 'No modo «bloqueado com aviso», preencha a mensagem exibida no painel da paróquia.',
        variant: 'destructive',
      });
      return;
    }
    saveFeaturesMutation.mutate();
  };

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <Typography color="error">Erro ao carregar paróquias</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
    <Box>
      <Typography variant="h4" gutterBottom>Gestão de Paróquias</Typography>
      <Typography variant="body1" gutterBottom>
        Gerencie paróquias cadastradas, aprove novos registros e monitore atividades
      </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => {
              setListTab('recipientApproval');
              setFilters((prev) => ({ ...prev, page: 1 }));
            }}
            startIcon={<CloudSyncIcon />}
          >
            Fila: aprovação recebedor
          </Button>
          <Button variant="text" color="primary" onClick={() => setOnboardingOpen(true)} startIcon={<MailOutlineIcon />}>
            Modal: lembretes por e-mail
          </Button>
          <Button variant="contained" color="primary" onClick={handleOpenCreate}>
            Nova Paróquia
          </Button>
        </Box>
      </Box>

      <Tabs
        value={listTab}
        onChange={handleListTabChange}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        variant="scrollable"
        allowScrollButtonsMobile
      >
        <Tab label="Todas as paróquias" value="all" />
        <Tab label="Aprovação recebedor" value="recipientApproval" />
        <Tab label="Status split Pagarme" value="splitStatus" />
        <Tab label="Divergência: email paróquia × login do admin" value="emailMismatch" />
      </Tabs>

      {listTab === 'splitStatus' && (
        <Box sx={{ mb: 4 }}>
          <PagarmeSplitStatusPanel />
        </Box>
      )}

      {listTab === 'recipientApproval' && (
        <Card sx={{ mb: 4 }}>
          <CardHeader
            title="Fila: criar / sincronizar recebedor"
            subheader={recipientQueueSubheader}
            action={
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                disabled={approvalQueueQuery.isFetching}
                onClick={() => approvalQueueQuery.refetch()}
              >
                Atualizar agora
              </Button>
            }
          />
          <CardContent>
            <FormControl size="small" sx={{ minWidth: { xs: 1, sm: 360 }, width: { xs: 1, sm: 'auto' }, mb: 2 }}>
              <InputLabel id="recipient-queue-filter">Filtrar lista</InputLabel>
              <Select<RecipientQueueView>
                labelId="recipient-queue-filter"
                label="Filtrar lista"
                value={recipientQueueView}
                onChange={(e) => setRecipientQueueView(e.target.value as RecipientQueueView)}
              >
                <MenuItem value="pending">Sem recebedor pronto (fila principal)</MenuItem>
                <MenuItem value="ready">Recebedor pronto — aprovado / sync OK</MenuItem>
                <MenuItem value="error">Onboarding local: erro</MenuItem>
                <MenuItem value="draft">Rascunho (draft)</MenuItem>
                <MenuItem value="pending_sync">Aguardando sync (pending_sync)</MenuItem>
                <MenuItem value="provider_refused">Pagar.me: recusa / bloqueio</MenuItem>
              </Select>
            </FormControl>
            <Alert severity="info" variant="outlined" sx={{ mb: 2, py: 1 }}>
              <Typography variant="body2" color="text.secondary">
                A tabela recarrega sozinha (~45s). <strong style={{ color: 'inherit' }}>Sincronizar</strong> envia ao
                Pagar.me. Ícones: validação local, consulta na API (só com ID já criado), abrir edição dos dados.
              </Typography>
            </Alert>
            {approvalQueueQuery.isLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : (
              <TableContainer
                sx={{
                  maxWidth: '100%',
                  overflowX: 'auto',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                }}
              >
                <Table size="small" sx={{ '& .MuiTableCell-root': { verticalAlign: 'middle' } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover', py: 1.25, minWidth: 200 }}>
                        Paróquia
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover', py: 1.25, whiteSpace: 'nowrap' }}>
                        Cidade / UF
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover', py: 1.25, whiteSpace: 'nowrap' }}>
                        Cadastro
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover', py: 1.25 }}>Status local</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover', py: 1.25, minWidth: 160 }}>
                        Pagar.me
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'action.hover', py: 1.25, maxWidth: 200 }}>
                        Último erro
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, bgcolor: 'action.hover', py: 1.25, width: 1, whiteSpace: 'nowrap' }}
                      >
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(
                      (approvalQueueQuery.data?.data?.parishes || []) as RecipientApprovalRow[]
                    ).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7}>
                          <Typography variant="body2" color="text.secondary">
                            {recipientQueueEmptyHint}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      ((approvalQueueQuery.data?.data?.parishes || []) as RecipientApprovalRow[]).map((row) => (
                        <TableRow key={String(row._id)} hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
                          <TableCell sx={{ py: 1.5 }}>
                            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>
                              {row.name}
                            </Typography>
                            {parishContactLines(row.email, row.primaryAdminEmail)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {[row.city, row.state].filter(Boolean).join(' / ') || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                              {row.createdAt
                                ? new Date(row.createdAt).toLocaleString('pt-BR', {
                                    dateStyle: 'short',
                                    timeStyle: 'short',
                                  })
                                : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell>{onboardingStatusChip(row.onboardingStatus)}</TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ lineHeight: 1.35 }}>
                              {row.providerStatus
                                ? providerStatusLabel(row.providerStatus)
                                : row.pagarMeRecipientId
                                  ? 'Aguardando consulta'
                                  : 'Ainda sem ID'}
                            </Typography>
                            {row.pagarMeRecipientId ? (
                              <Typography
                                variant="caption"
                                component="code"
                                sx={{
                                  display: 'block',
                                  mt: 0.25,
                                  wordBreak: 'break-all',
                                  color: 'text.secondary',
                                  fontSize: '0.7rem',
                                }}
                              >
                                {row.pagarMeRecipientId}
                              </Typography>
                            ) : null}
                            {row.providerStatusCheckedAt ? (
                              <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.25 }}>
                                {new Date(row.providerStatusCheckedAt).toLocaleString('pt-BR')}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 200 }}>
                            {row.lastSyncError ? (
                              <Tooltip title={row.lastSyncError} placement="top-start">
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    cursor: 'help',
                                  }}
                                >
                                  {row.lastSyncError}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography variant="caption" color="text.disabled">
                                Nenhum
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1 }}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="flex-end"
                              alignItems="center"
                              flexWrap="nowrap"
                            >
                              <Tooltip title="Validar dados (sem chamar o Pagar.me)">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={adminSyncRecipientMutation.isPending}
                                    onClick={() =>
                                      adminSyncRecipientMutation.mutate({ id: String(row._id), dryRun: true })
                                    }
                                    sx={{ color: 'text.secondary' }}
                                    aria-label="Validar"
                                  >
                                    <FactCheckOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Button
                                variant="contained"
                                size="small"
                                disableElevation
                                disabled={adminSyncRecipientMutation.isPending}
                                onClick={() =>
                                  adminSyncRecipientMutation.mutate({ id: String(row._id), dryRun: false })
                                }
                                startIcon={<CloudSyncIcon sx={{ fontSize: 18 }} />}
                                sx={{ px: 1.25, minHeight: 32, textTransform: 'none', fontWeight: 600 }}
                              >
                                Sincronizar
                              </Button>
                              <Tooltip
                                title={
                                  row.pagarMeRecipientId
                                    ? 'Atualizar status na API do Pagar.me'
                                    : 'Disponível após criar o recebedor (Sincronizar)'
                                }
                              >
                                <span>
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    disabled={
                                      !row.pagarMeRecipientId || refreshProviderStatusMutation.isPending
                                    }
                                    onClick={() => refreshProviderStatusMutation.mutate(String(row._id))}
                                    aria-label="Consultar status na API"
                                  >
                                    <RefreshIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Abrir revisão dos dados do recebedor">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  component={RouterLink}
                                  to={`/parishes/pagarme/${encodeURIComponent(String(row._id))}`}
                                  aria-label="Dados do recebedor"
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      {listTab === 'emailMismatch' && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          O email da paróquia é tratado como <strong>contato institucional</strong>; o login e a recuperação de
          senha usam só o email do usuário admin. Esta lista mostra quando esses dois divergem (ex.: secretaria
          vs. login pessoal). Admin principal = primeiro <code>parish_admin</code> ativo por data de criação.
          A busca inclui nome, cidade, email da paróquia e email do admin.
        </Typography>
      )}

      <Dialog open={onboardingOpen} onClose={() => setOnboardingOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Paróquias sem recebedor Pagar.me pronto</DialogTitle>
        <DialogContent>
          {onboardingPendingQuery.isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Paróquia</TableCell>
                  <TableCell>Cidade/UF</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(onboardingPendingQuery.data?.data?.parishes || []).map((row: any) => (
                  <TableRow key={String(row._id)}>
                    <TableCell>{row.name}</TableCell>
                    <TableCell>
                      {row.city} / {row.state}
                    </TableCell>
                    <TableCell>
                      {row.hasPixWithdrawalKey ? 'Pix OK, falta sync' : 'Sem Pix'}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        component={RouterLink}
                        to={`/parishes/pagarme/${encodeURIComponent(String(row._id))}`}
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => setOnboardingOpen(false)}
                      >
                        Corrigir dados
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={sendOnboardingMutation.isPending}
                        onClick={() => sendOnboardingMutation.mutate(String(row._id))}
                      >
                        Enviar e-mail
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOnboardingOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de criação de paróquia */}
      <Dialog open={openCreate} onClose={handleCloseCreate} maxWidth="md" fullWidth>
        <DialogTitle>Criar Nova Paróquia</DialogTitle>
        <DialogContent>
          <ParishRegistrationRequest embedded />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreate}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de detalhes da paróquia */}
      <Dialog open={!!parishForDetails} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Detalhes — {parishForDetails?.name}</DialogTitle>
        <DialogContent>
          {parishDetailModalLoading || !parishDetailModal ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              {parishDetailModal.emailAlignment &&
                !parishDetailModal.emailAlignment.matchesPrimaryAdmin && (
                  <Alert severity="warning" sx={{ alignItems: 'center' }}>
                    Email da paróquia ({parishDetailModal.emailAlignment.parishEmail || '—'}) difere do
                    login do admin principal ({parishDetailModal.emailAlignment.primaryAdminEmail || '—'}).
                    Isso confunde recuperação de senha e listagens.
                    <Button
                      size="small"
                      variant="contained"
                      sx={{ mt: 1, display: 'block' }}
                      disabled={alignEmailMutation.isPending}
                      onClick={() =>
                        parishForDetails?._id &&
                        alignEmailMutation.mutate(String(parishForDetails._id))
                      }
                    >
                      Igualar email da paróquia ao do admin
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 1, display: 'block' }}
                      disabled={alignPrimaryAdminEmailMutation.isPending}
                      onClick={() =>
                        parishForDetails?._id &&
                        alignPrimaryAdminEmailMutation.mutate(String(parishForDetails._id))
                      }
                    >
                      Usar e-mail da paróquia como login do admin
                    </Button>
                  </Alert>
                )}
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Email (cadastro paróquia)
                </Typography>
                <Typography>{parishDetailModal.email || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Admin principal (login)
                </Typography>
                <Typography>
                  {parishDetailModal.parishAdmin?.email ||
                    parishDetailModal.emailAlignment?.primaryAdminEmail ||
                    '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Telefone</Typography>
                <Typography>{parishDetailModal.phone || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Cidade / Estado</Typography>
                <Typography>
                  {parishDetailModal.city && parishDetailModal.state
                    ? `${parishDetailModal.city} / ${parishDetailModal.state}`
                    : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">Endereço</Typography>
                <Typography>{parishDetailModal.address || '—'}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={async () => {
              if (!parishForDetails?._id) return;
              try {
                const docs = await legalService.listSignedDocuments({
                  relatedType: 'organization',
                  relatedId: parishForDetails._id,
                  templateKey: 'platform-service-contract',
                });
                const doc = docs[0];
                if (!doc) {
                  toast({ title: 'Nenhum contrato assinado encontrado', variant: 'destructive' });
                  return;
                }
                const blob = await legalService.downloadSignedDocumentAdmin(doc.id);
                triggerBlobDownload(blob, `contrato-${parishForDetails.name}.pdf`);
              } catch {
                toast({ title: 'Não foi possível baixar o contrato', variant: 'destructive' });
              }
            }}
          >
            Baixar contrato assinado
          </Button>
          <Button onClick={handleCloseDetails}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de funcionalidades por paróquia */}
      <Dialog open={!!parishForFeatures} onClose={handleCloseFeatures} maxWidth="sm" fullWidth>
        <DialogTitle>
          Funcionalidades — {parishForFeatures?.name}
        </DialogTitle>
        <DialogContent dividers sx={{ maxHeight: '70vh' }}>
          {parishFeaturesLoading || !parishDetailResponse ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" fontWeight={700}>
                  E-mail de login (admin principal)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Mesmo critério do painel: sem confirmação o admin recebe bloqueio{' '}
                  <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    EMAIL_NOT_VERIFIED
                  </Typography>
                  . Use apenas quando tiver certeza de que o e-mail é válido.
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <Typography component="span" variant="caption" color="text.secondary" display="block">
                    Login
                  </Typography>
                  {parishDetailResponse.parishAdmin?.email ||
                    parishDetailResponse.emailAlignment?.primaryAdminEmail ||
                    '—'}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap" sx={{ mt: 1.5 }}>
                  {primaryAdminEmailVerifiedChip(parishDetailResponse.parishAdmin?.emailVerifiedAt)}
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={
                      verifyPrimaryAdminEmailMutation.isPending ||
                      !parishDetailResponse.parishAdmin ||
                      !!parishDetailResponse.parishAdmin.emailVerifiedAt
                    }
                    onClick={() =>
                      parishForFeatures?._id &&
                      verifyPrimaryAdminEmailMutation.mutate(String(parishForFeatures._id))
                    }
                  >
                    {verifyPrimaryAdminEmailMutation.isPending ? 'Salvando…' : 'Marcar e-mail como validado'}
                  </Button>
                </Stack>
                {!parishDetailResponse.parishAdmin && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    Nenhum administrador principal ativo — não é possível marcar verificação.
                  </Typography>
                )}
              </Box>

              <Divider />

              <Box
                sx={{
                  pl: 1.5,
                  borderLeft: '3px solid',
                  borderColor: 'secondary.main',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Reembolso liberado
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Quando ativo, a casa pode solicitar reembolso no detalhe da inscrição (com senha). O estorno
                    só ocorre após aceite no painel admin. Continua bloqueado se não houver saldo disponível
                    (após saque).
                  </Typography>
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featuresDraft.allowRefundRequest}
                      onChange={(e) =>
                        setFeaturesDraft((prev) => ({
                          ...prev,
                          allowRefundRequest: e.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="Permitir solicitar reembolso no painel da paróquia"
                />
              </Box>

              <Divider />

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  Trava do módulo Saque (painel da paróquia)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Independente de qual administrador tenha permissão de saque. A trava global da plataforma continua
                  valendo por cima.
                </Typography>
                <FormControl component="fieldset" variant="standard" sx={{ ml: 0 }}>
                  <FormLabel component="legend" sx={{ typography: 'body2', fontWeight: 600, mb: 0.5 }}>
                    Como o painel deve se comportar?
                  </FormLabel>
                  <RadioGroup
                    value={featuresDraft.withdrawalPanelMode}
                    onChange={(e) =>
                      setFeaturesDraft((p) => ({
                        ...p,
                        withdrawalPanelMode: e.target.value as 'released' | 'hidden' | 'message',
                      }))
                    }
                  >
                    <FormControlLabel
                      value="released"
                      control={<Radio size="small" />}
                      label="Liberado — aba Saque e fluxo normais"
                    />
                    <FormControlLabel
                      value="hidden"
                      control={<Radio size="small" />}
                      label="Oculto — não aparece Saque no menu nem na rota (como se o módulo não existisse)"
                    />
                    <FormControlLabel
                      value="message"
                      control={<Radio size="small" />}
                      label="Bloqueado com mensagem — aparece Saque, mas só mostra o aviso abaixo"
                    />
                  </RadioGroup>
                </FormControl>
                {featuresDraft.withdrawalPanelMode === 'message' && (
                  <TextField
                    label="Mensagem no painel"
                    placeholder="Ex.: Estamos regularizando repasses com a equipe financeira. Voltamos em breve."
                    value={featuresDraft.withdrawalBlockedMessage}
                    onChange={(e) =>
                      setFeaturesDraft((p) => ({ ...p, withdrawalBlockedMessage: e.target.value }))
                    }
                    multiline
                    minRows={3}
                    fullWidth
                    required
                    inputProps={{ maxLength: 2000 }}
                  />
                )}
              </Box>

              <Divider />

              <Box
                sx={{
                  pl: 1.5,
                  borderLeft: '3px solid',
                  borderColor: 'primary.main',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Saque liberado
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    O padrão é liberado. Desmarque para ocultar o painel de saque deste administrador. A trava
                    global da plataforma continua podendo desativar saques para todos.
                  </Typography>
                </Box>
                {!parishAdmins?.length ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum administrador de paróquia cadastrado.
                  </Typography>
                ) : (
                  parishAdmins.map((admin) => (
                    <Box key={String(admin._id)}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!adminsDraft[String(admin._id)]}
                            onChange={(e) =>
                              setAdminsDraft((prev) => ({
                                ...prev,
                                [String(admin._id)]: e.target.checked,
                              }))
                            }
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" component="span" display="block">
                              {admin.name || 'Administrador'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {admin.email}
                            </Typography>
                          </Box>
                        }
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
                        Pode acessar o módulo de saque no painel da paróquia
                      </Typography>
                    </Box>
                  ))
                )}

                <Divider sx={{ my: 1 }} />

                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Multi-organização
                    </Typography>
                    <Tooltip
                      title="Mesmo login, vários CNPJs. Só no painel logado; cadastro público continua travado. Clique no livro para ver o guia completo."
                      arrow
                    >
                      <IconButton size="small" aria-label="Informações sobre multi-organização" sx={{ p: 0.35 }}>
                        <InfoOutlinedIcon fontSize="inherit" color="action" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Abrir documentação">
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to="/documentation#multi-organizacao"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Ver documentação de multi-organização"
                        sx={{ p: 0.35, color: 'text.secondary' }}
                      >
                        <MenuBookOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Libera o botão “Adicionar organização” no painel logado (mesmo usuário, CNPJ novo). O
                    formulário público continua bloqueando CPF/e-mail duplicados.
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/documentation#multi-organizacao"
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    variant="text"
                    startIcon={<MenuBookOutlinedIcon sx={{ fontSize: 16 }} />}
                    sx={{
                      mt: 0.5,
                      px: 0.5,
                      minWidth: 0,
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      color: 'text.secondary',
                      '&:hover': { bgcolor: 'transparent', color: 'primary.main' },
                    }}
                  >
                    Ver regras e fluxo
                  </Button>
                </Box>
                {!parishAdmins?.length ? null : (
                  parishAdmins.map((admin) => (
                    <Box key={`org-${String(admin._id)}`}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={!!additionalOrgsDraft[String(admin._id)]}
                            onChange={(e) =>
                              setAdditionalOrgsDraft((prev) => ({
                                ...prev,
                                [String(admin._id)]: e.target.checked,
                              }))
                            }
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" component="span" display="block">
                              {admin.name || 'Administrador'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {admin.email}
                            </Typography>
                          </Box>
                        }
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4, mt: -0.5 }}>
                        Pode adicionar organizações extras no painel
                      </Typography>
                    </Box>
                  ))
                )}

                <Box sx={{ mt: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={featuresDraft.allowEarlyWithdrawalRequest}
                        disabled={!hasAnyWithdrawalAccess}
                        onChange={(e) =>
                          setFeaturesDraft((prev) => ({
                            ...prev,
                            allowEarlyWithdrawalRequest: e.target.checked,
                          }))
                        }
                        color="primary"
                      />
                    }
                    label="Permitir solicitar saque antecipado antes do evento"
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 0 }}>
                    {hasAnyWithdrawalAccess
                      ? 'Quando ativo, administradores com saque liberado podem antecipar valores antes da realização do evento.'
                      : 'Disponível quando pelo menos um administrador tiver acesso ao saque.'}
                  </Typography>
                </Box>
              </Box>

              <Divider />

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featuresDraft.allowCreateSubgroup}
                      onChange={(e) =>
                        setFeaturesDraft((prev) => ({
                          ...prev,
                          allowCreateSubgroup: e.target.checked,
                          maxSubParishes: e.target.checked ? Math.max(prev.maxSubParishes, 1) : prev.maxSubParishes,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="Permitir criar setores"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  A organização cria acessos com nome, senha e descrição. Cada setor entra pelo link dela.
                </Typography>
                {featuresDraft.allowCreateSubgroup && (
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
                    <Typography variant="body2" component="label" htmlFor="max-subparishes">
                      Limite
                    </Typography>
                    <TextField
                      id="max-subparishes"
                      type="number"
                      size="small"
                      value={Math.max(1, featuresDraft.maxSubParishes || 1)}
                      onChange={(e) => {
                        const next = Math.max(1, Math.min(100, Number(e.target.value) || 1));
                        setFeaturesDraft((prev) => ({ ...prev, maxSubParishes: next }));
                      }}
                      inputProps={{ min: 1, max: 100, 'aria-label': 'Limite de setores' }}
                      sx={{ width: 88 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      setores ativos
                    </Typography>
                  </Stack>
                )}
              </Box>

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featuresDraft.customDomainPagesEnabled}
                      onChange={(e) =>
                        setFeaturesDraft((prev) => ({
                          ...prev,
                          customDomainPagesEnabled: e.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="Página personalizada (domínio próprio)"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 0 }}>
                  Libera a organização para usar domínio próprio nos eventos (configurado em Páginas personalizadas).
                </Typography>
              </Box>

              <Divider />

              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={featuresDraft.pagarmePixCheckoutEnabled}
                      onChange={(e) =>
                        setFeaturesDraft((prev) => ({
                          ...prev,
                          pagarmePixCheckoutEnabled: e.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="Permitir Pix via Pagar.me quando recebedor estiver pronto"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 0 }}>
                  Se desmarcado, o Pix desta paróquia permanece pelo Mercado Pago (fallback manual), mesmo com
                  onboarding concluído.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeatures}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSaveFeatures}
            disabled={
              saveFeaturesMutation.isPending || parishFeaturesLoading || !parishDetailResponse
            }
          >
            {saveFeaturesMutation.isPending ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </Dialog>

      {listTab !== 'recipientApproval' && listTab !== 'splitStatus' && (
        <>
          {/* Filtros */}
          <Card sx={{ mb: 4 }}>
        <CardHeader title={
          <Box display="flex" alignItems="center" gap={1}>
            <FilterList /> Filtros
          </Box>
        } />
        <CardContent>
          <Box display="grid" gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))" gap={2}>
            <TextField
              label="Buscar"
              variant="outlined"
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="active">Ativa</MenuItem>
                <MenuItem value="pending">Pendente</MenuItem>
                <MenuItem value="rejected">Rejeitada</MenuItem>
                <MenuItem value="inactive">Inativa</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Estado</InputLabel>
              <Select
                label="Estado"
                value={filters.state}
                onChange={(e) => handleFilterChange('state', e.target.value)}
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="SP">São Paulo</MenuItem>
                <MenuItem value="RJ">Rio de Janeiro</MenuItem>
                <MenuItem value="MG">Minas Gerais</MenuItem>
                <MenuItem value="RS">Rio Grande do Sul</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardHeader
          title={
            listTab === 'emailMismatch'
              ? 'Paróquias com email divergente'
              : 'Paróquias Cadastradas'
          }
        />
        <CardContent>
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : !parishesData?.data?.parishes?.length ? (
            <Typography variant="body2" color="text.secondary" py={2}>
              {listTab === 'emailMismatch'
                ? 'Nenhuma paróquia com divergência entre email da paróquia e email de login do admin principal.'
                : 'Nenhuma paróquia encontrada com os filtros atuais.'}
            </Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Cidade / Estado</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>E-mail verificado (login)</TableCell>
                  {listTab === 'emailMismatch' ? (
                    <>
                      <TableCell>Email (paróquia)</TableCell>
                      <TableCell>Email login (admin)</TableCell>
                      <TableCell>Telefone</TableCell>
                    </>
                  ) : (
                    <TableCell>Login / contato</TableCell>
                  )}
                  {listTab === 'all' ? <TableCell>Split Pagarme</TableCell> : null}
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parishesData?.data?.parishes?.map((parish: ParishListRow) => (
                  <TableRow key={parish._id}>
                    <TableCell>{parish.name}</TableCell>
                    <TableCell>{parish.status || 'active'}</TableCell>
                    <TableCell>
                      {parish.city && parish.state ? `${parish.city} / ${parish.state}` : '—'}
                    </TableCell>
                    <TableCell>{primaryAdminEmailVerifiedChip(parish.primaryAdminEmailVerifiedAt)}</TableCell>
                    {listTab === 'emailMismatch' ? (
                      <>
                        <TableCell>{parish.email || '—'}</TableCell>
                        <TableCell>{parish.primaryAdminEmail || '—'}</TableCell>
                        <TableCell>{parish.phone || '—'}</TableCell>
                      </>
                    ) : (
                      <TableCell sx={{ minWidth: 200 }}>
                        {parishContactLines(parish.email, parish.primaryAdminEmail)}
                        {parish.phone ? (
                          <Typography variant="caption" color="text.disabled" display="block">
                            Tel: {parish.phone}
                          </Typography>
                        ) : null}
                      </TableCell>
                    )}
                    {listTab === 'all' ? (
                      <TableCell sx={{ minWidth: 180 }}>{parishSplitStatusCell(parish)}</TableCell>
                    ) : null}
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDetails(parish)}
                        title="Ver detalhes"
                        aria-label="Ver detalhes"
                      >
                        <Visibility />
                      </IconButton>
                      {listTab === 'emailMismatch' ? (
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ ml: 0.5, mr: 0.5, whiteSpace: 'nowrap' }}
                          disabled={alignPrimaryAdminEmailMutation.isPending}
                          onClick={() => alignPrimaryAdminEmailMutation.mutate(String(parish._id))}
                        >
                          Login = contato
                        </Button>
                      ) : null}
                      <IconButton
                        size="small"
                        component={RouterLink}
                        to={`/parishes/pagarme/${encodeURIComponent(String(parish._id))}`}
                        title="Revisão Pagarme"
                        aria-label="Revisão Pagarme"
                      >
                        <FactCheckOutlinedIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenFeatures(parish)}
                        title="Funcionalidades"
                        aria-label="Funcionalidades"
                      >
                        <SettingsIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </>
      )}
    </Box>
  );
};

export default ParishesMUI;
